/**
 * Stock page payload types derived from squaber-backend serializers.
 * Unknown / unused backend fields are left as `unknown` rather than invented.
 */

import type { ReactNode } from "react";

export type AsyncSlot<T> = {
  data: T | null;
  loading: boolean;
  error: unknown;
};

export type StockBasicInfo = {
  share_amount?: number;
  capitalization?: number;
  [key: string]: unknown;
};

/** `StockSerializer` fields used on the stock page */
export type StockSummary = {
  id: number;
  ticker: string;
  market: string;
  name: string;
  short_name: string;
  currency: string;
  is_index: boolean;
  pipsize: number | string;
  multiplier: number | string;
  indexes: number[];
  recommended: boolean;
  attention: unknown;
  attention_updated: unknown;
  is_active: boolean;
  basic_info: StockBasicInfo | Record<string, never>;
  description_activities?: string;
  average_volume?: unknown;
  comparable?: unknown;
};

/** Quote subset returned by `StockWithQuoteSerializer.get_quote` */
export type StockQuote = {
  close: number;
  change: number | null;
};

/** `app.stocks.stock` → `StockWithQuoteSerializer` */
export type StockPayload = {
  stock: StockSummary;
  quote: StockQuote | Record<string, never>;
  old_quote: number | null;
  latest_quote_datetime_utc: string | null;
};

export type Situation = {
  description: string;
  description_html: string;
};

/** Fields the UI reads from `SignalSerializer` (`fields = "__all__"`) */
export type Signal = {
  type: "buy" | "sell" | string;
  result?: string;
  tp?: unknown;
  sl?: unknown;
  price?: unknown;
  max_profit?: unknown;
  deactivation_price?: unknown;
  stock?: unknown;
  strategy?: unknown;
  [key: string]: unknown;
};

export type TransactionStrategy = {
  [key: string]: unknown;
};

export type RateOfReturn = {
  [key: string]: unknown;
};

export type Stockholder = {
  [key: string]: unknown;
};

/**
 * `app.stocks.singleStock` at `optimized=6`.
 * Gated sections appear only as flags in `visible_optional_sections`.
 */
export type StockPageData = {
  ai_analysis_availability: string[];
  visible_optional_sections: string[];
  situation: Situation;
  signal: Signal | false;
  transaction_strategies: TransactionStrategy[];
  rate_of_returns: RateOfReturn[];
  stockholders: Stockholder[];
};

/** `PriceAlertSerializer` */
export type PriceAlert = {
  id: number;
  price: string;
  comment: string | null;
  sent_date: string | null;
};

/** `RecommendationSerializer` */
export type StockRecommendation = {
  issue_date: string;
  institution: string;
  kind: string;
  target_price: string | number | null;
  issue_price: number | null;
};

export type StockRecommendationsPayload = {
  recommendations: StockRecommendation[];
  more_recommendations: boolean;
};

/** `StockSituationResultsSerializer` — UI reads these keys */
export type FinancialSituation = {
  stock_classification: unknown;
  all_stocks_count: number;
  stock_rank: number;
  parameters_groups_classification: unknown[];
  warnings: unknown[];
  balance_date: string | null;
};

/** One media-monitor report row (paginated `results` item) */
export type MediaMonitorEntry = {
  pub_datetime: string;
  title: string;
  link: string | null;
  description: string | null;
  source: string;
  source_message?: string;
  provider?: string;
  attachments?: unknown[];
  is_premium: boolean;
  permissions?: unknown;
  [key: string]: unknown;
};

export type MediaMonitorData = {
  messages: MediaMonitorEntry[];
  is_last_page: boolean;
};

/** Stock tags list item (shape varies; UI needs `id` and tag display fields) */
export type StockTag = {
  id: number;
  name?: string;
  [key: string]: unknown;
};

export type StockPageProviderProps = {
  market: string;
  ticker: string;
  active: boolean;
  children: ReactNode;
};

export type StockPageContextValue = {
  market: string;
  ticker: string;
  active: boolean;
  stockId: number | null;
  stockName: string | null;

  pageData: AsyncSlot<StockPageData>;
  stock: AsyncSlot<StockPayload>;
  financialSituation: AsyncSlot<FinancialSituation>;
  /** Section visibility only — list/pagination lives in MediaMonitorWrapper. */
  hasMediaMonitor: boolean;
  priceAlerts: AsyncSlot<PriceAlert[]>;
  stockTags: AsyncSlot<StockTag[]>;
  recommendations: AsyncSlot<StockRecommendation[]>;
  hasMoreRecommendations: boolean;
  moreRecommendationsLoading: boolean;

  loadMoreRecommendations: () => Promise<void>;
  loadFinancialSituation: () => Promise<void>;
  reloadPriceAlerts: () => Promise<void>;
  reloadTags: () => Promise<void>;
};

export function emptySlot<T>(loading = false): AsyncSlot<T> {
  return { data: null, loading, error: null };
}
